import React, { useState, useEffect } from 'react';
import {
  Card,
  Row,
  Col,
  Typography,
  Button,
  Space,
  Table,
  Modal,
  Form,
  Input,
  message,
  Statistic,
  Tag,
  Popconfirm,
  Tooltip,
  Switch,
  Divider,
  Alert,
  Badge,
  Descriptions
} from 'antd';
import {
  PlusOutlined,
  ReloadOutlined,
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
  ApiOutlined,
  LinkOutlined,
  SecurityScanOutlined,
  KeyOutlined
} from '@ant-design/icons';
import webhookEndpointsService from '../../services/webhookEndpointsService';
import '../../styles/global-input-fixes.css';

const { Title, Text } = Typography;
const { TextArea } = Input;

const WebhooksManagement = () => {
  const [endpoints, setEndpoints] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingEndpoint, setEditingEndpoint] = useState(null);
  const [viewModalVisible, setViewModalVisible] = useState(false);
  const [viewingEndpoint, setViewingEndpoint] = useState(null);
  const [form] = Form.useForm();
  const [statistics, setStatistics] = useState({
    total: 0,
    active: 0,
    inactive: 0
  });

  // Colunas da tabela de endpoints
  const endpointColumns = [
    {
      title: 'Nome',
      dataIndex: 'name',
      key: 'name',
      render: (name, record) => (
        <Space>
          <ApiOutlined style={{ color: record.isActive ? '#52c41a' : '#d9d9d9' }} />
          <Text strong>{name}</Text>
        </Space>
      )
    },
    {
      title: 'Endpoint',
      dataIndex: 'endpoint',
      key: 'endpoint',
      render: () => (
        <Tooltip title="/webhook/xml">
          <Text copyable={{ text: `${import.meta.env.VITE_API_URL}/webhook/xml` }} style={{ maxWidth: 200 }}>
            <LinkOutlined /> /webhook/xml
          </Text>
        </Tooltip>
      )
    },
    {
      title: 'Origens Permitidas',
      dataIndex: 'allowedOrigins',
      key: 'allowedOrigins',
      render: (origins) => {
        if (!origins || origins.length === 0) {
          return <Tag color="orange">Todas as origens</Tag>;
        }
        return (
          <Space wrap>
            {origins.slice(0, 2).map((origin, index) => (
              <Tag key={index} size="small">{origin}</Tag>
            ))}
            {origins.length > 2 && (
              <Tag size="small">+{origins.length - 2} mais</Tag>
            )}
          </Space>
        );
      }
    },
    {
      title: 'Status',
      dataIndex: 'isActive',
      key: 'isActive',
      render: (isActive) => (
        <Badge
          status={isActive ? 'success' : 'default'}
          text={isActive ? 'Ativo' : 'Inativo'}
        />
      )
    },
    {
      title: 'Token',
      dataIndex: 'token',
      key: 'token',
      render: (token) => (
        <Tooltip title="Clique para copiar o token">
          <Button
            type="text"
            icon={<KeyOutlined />}
            onClick={() => {
              navigator.clipboard.writeText(token);
              message.success('Token copiado!');
            }}
          />
        </Tooltip>
      )
    },
    {
      title: 'Último Uso',
      dataIndex: 'lastUsedAt',
      key: 'lastUsedAt',
      render: (date) => date ? new Date(date).toLocaleDateString('pt-BR') : 'Nunca'
    },
    {
      title: 'Ações',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Tooltip title="Visualizar">
            <Button
              type="text"
              icon={<EyeOutlined />}
              onClick={() => handleView(record)}
            />
          </Tooltip>
          <Tooltip title="Editar">
            <Button
              type="text"
              icon={<EditOutlined />}
              onClick={() => handleEdit(record)}
            />
          </Tooltip>
          <Tooltip title="Regenerar Token">
            <Button
              type="text"
              icon={<KeyOutlined />}
              onClick={() => handleRegenerateToken(record)}
            />
          </Tooltip>
          <Tooltip title={record.isActive ? 'Desativar' : 'Ativar'}>
            <Switch
              checked={record.isActive}
              onChange={(checked) => handleToggleActive(record, checked)}
              size="small"
            />
          </Tooltip>
          <Popconfirm
            title="Tem certeza que deseja excluir este endpoint?"
            onConfirm={() => handleDelete(record.id)}
            okText="Sim"
            cancelText="Não"
          >
            <Tooltip title="Excluir">
              <Button
                type="text"
                danger
                icon={<DeleteOutlined />}
              />
            </Tooltip>
          </Popconfirm>
        </Space>
      )
    }
  ];

  // Carregar endpoints
  const loadEndpoints = async () => {
    setLoading(true);
    try {
      const endpointsData = await webhookEndpointsService.getEndpoints();

      // Verificar se endpointsData é um array válido
      if (!Array.isArray(endpointsData)) {
        console.error('Dados da API não são um array:', endpointsData);
        setEndpoints([]);
        setStatistics({ total: 0, active: 0, inactive: 0 });
        return;
      }

      // Processar dados para garantir que allowedOrigins seja um array
      const processedEndpoints = endpointsData.map(endpoint => ({
        ...endpoint,
        allowedOrigins: endpoint.allowedOrigins
          ? (typeof endpoint.allowedOrigins === 'string'
            ? JSON.parse(endpoint.allowedOrigins)
            : endpoint.allowedOrigins)
          : []
      }));

      setEndpoints(processedEndpoints);

      // Calcular estatísticas
      const total = processedEndpoints.length;
      const active = processedEndpoints.filter(e => e.isActive).length;
      setStatistics({
        total,
        active,
        inactive: total - active
      });
    } catch (error) {
      message.error('Erro ao carregar endpoints');
      console.error('Erro ao carregar endpoints:', error);
    } finally {
      setLoading(false);
    }
  };

  // Criar/Editar endpoint
  const handleSubmit = async (values) => {
    try {
      const submitData = {
        ...values,
        endpoint: '/nota', // Fixo para a única rota disponível
        allowedOrigins: values.allowedOrigins ? values.allowedOrigins.split(',').map(o => o.trim()).filter(o => o) : []
      };

      if (editingEndpoint) {
        await webhookEndpointsService.updateEndpoint(editingEndpoint.id, submitData);
        message.success('Endpoint atualizado com sucesso!');
      } else {
        await webhookEndpointsService.createEndpoint(submitData);
        message.success('Endpoint criado com sucesso!');
      }

      setModalVisible(false);
      setEditingEndpoint(null);
      form.resetFields();
      loadEndpoints();
    } catch (error) {
      message.error(editingEndpoint ? 'Erro ao atualizar endpoint' : 'Erro ao criar endpoint');
      console.error('Erro ao salvar endpoint:', error);
    }
  };

  // Visualizar endpoint
  const handleView = (endpoint) => {
    setViewingEndpoint(endpoint);
    setViewModalVisible(true);
  };

  // Editar endpoint
  const handleEdit = (endpoint) => {
    setEditingEndpoint(endpoint);

    // Processar allowedOrigins para o formulário
    let allowedOriginsText = '';
    if (endpoint.allowedOrigins) {
      const origins = typeof endpoint.allowedOrigins === 'string'
        ? JSON.parse(endpoint.allowedOrigins)
        : endpoint.allowedOrigins;
      allowedOriginsText = Array.isArray(origins) ? origins.join(', ') : '';
    }

    form.setFieldsValue({
      name: endpoint.name,
      description: endpoint.description,
      isActive: endpoint.isActive,
      allowedOrigins: allowedOriginsText
    });
    setModalVisible(true);
  };

  // Excluir endpoint
  const handleDelete = async (id) => {
    try {
      await webhookEndpointsService.deleteEndpoint(id);
      message.success('Endpoint excluído com sucesso!');
      loadEndpoints();
    } catch (error) {
      message.error('Erro ao excluir endpoint');
      console.error('Erro ao excluir endpoint:', error);
    }
  };

  // Ativar/Desativar endpoint
  const handleToggleActive = async (endpoint, checked) => {
    try {
      await webhookEndpointsService.updateEndpoint(endpoint.id, {
        isActive: checked
      });
      message.success(`Endpoint ${checked ? 'ativado' : 'desativado'} com sucesso!`);
      loadEndpoints();
    } catch (error) {
      message.error('Erro ao alterar status do endpoint');
      console.error('Erro ao alterar status:', error);
    }
  };

  // Regenerar token do endpoint
  const handleRegenerateToken = async (endpoint) => {
    try {
      const updatedEndpoint = await webhookEndpointsService.regenerateToken(endpoint.id);
      message.success('Token regenerado com sucesso!');

      // Copiar novo token para clipboard
      if (updatedEndpoint.token) {
        navigator.clipboard.writeText(updatedEndpoint.token);
        message.info('Novo token copiado para a área de transferência!');
      }

      loadEndpoints();
    } catch (error) {
      message.error('Erro ao regenerar token');
      console.error('Erro ao regenerar token:', error);
    }
  };

  // Abrir modal para novo endpoint
  const handleNew = () => {
    setEditingEndpoint(null);
    form.resetFields();
    setModalVisible(true);
  };

  // Fechar modal
  const handleCancel = () => {
    setModalVisible(false);
    setEditingEndpoint(null);
    form.resetFields();
  };

  // Fechar modal de visualização
  const handleViewCancel = () => {
    setViewModalVisible(false);
    setViewingEndpoint(null);
  };

  useEffect(() => {
    loadEndpoints();
  }, []);

  return (
    <div style={{ padding: '24px' }}>
      <Row gutter={[16, 16]}>
        <Col span={24}>
          <Card>
            <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
              <Col>
                <Title level={3} style={{ margin: 0 }}>
                  <ApiOutlined /> Gerenciamento de Endpoints Webhook
                </Title>
                <Text type="secondary">
                  Configure endpoints para receber dados de aplicações externas
                </Text>
              </Col>
              <Col>
                <Space>
                  <Button
                    icon={<ReloadOutlined />}
                    onClick={loadEndpoints}
                    loading={loading}
                  >
                    Atualizar
                  </Button>
                  <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={handleNew}
                  >
                    Novo Webhook
                  </Button>
                </Space>
              </Col>
            </Row>

            {/* Estatísticas */}
            <Row gutter={16} style={{ marginBottom: 24 }}>
              <Col xs={24} sm={8}>
                <Statistic
                  title="Total de Webhooks"
                  value={statistics.total}
                  prefix={<ApiOutlined />}
                />
              </Col>
              <Col xs={24} sm={8}>
                <Statistic
                  title="Webhooks Ativos"
                  value={statistics.active}
                  valueStyle={{ color: '#3f8600' }}
                  prefix={<KeyOutlined />}
                />
              </Col>
              <Col xs={24} sm={8}>
                <Statistic
                  title="Webhooks Inativos"
                  value={statistics.inactive}
                  valueStyle={{ color: '#cf1322' }}
                  prefix={<DeleteOutlined />}
                />
              </Col>
            </Row>

            <Divider />

            {/* Tabela de endpoints */}
            <Table
              columns={endpointColumns}
              dataSource={endpoints}
              rowKey="id"
              loading={loading}
              pagination={{
                pageSize: 10,
                showSizeChanger: true,
                showQuickJumper: true,
                showTotal: (total, range) =>
                  `${range[0]}-${range[1]} de ${total} endpoints`
              }}
              scroll={{ x: 1000 }}
            />
          </Card>
        </Col>
      </Row>

      {/* Modal de Criar/Editar Endpoint */}
      <Modal
        title={editingEndpoint ? 'Editar Endpoint' : 'Novo Endpoint'}
        open={modalVisible}
        onCancel={handleCancel}
        footer={null}
        width={600}
        style={{ top: 0 }}
        maskClosable={false}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          initialValues={{ isActive: true }}
        >
          <Form.Item
            name="name"
            label="Nome"
            rules={[{ required: true, message: 'Nome é obrigatório' }]}
          >
            <Input placeholder="Ex: Endpoint de Notas Fiscais" />
          </Form.Item>

          <Alert
            message="Endpoint Fixo"
            description="Este webhook utilizará automaticamente o endpoint: /v1/webhook/xml"
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
          />

          <Form.Item
            name="description"
            label="Descrição"
          >
            <TextArea
              rows={3}
              placeholder="Descrição opcional do endpoint"
            />
          </Form.Item>

          <Form.Item
            name="allowedOrigins"
            label="Origens Permitidas (opcional)"
            help="Deixe vazio para permitir todas as origens. Separe múltiplas origens por vírgula."
          >
            <Input placeholder="https://app1.com, https://app2.com" />
          </Form.Item>

          <Form.Item
            name="isActive"
            label="Status"
            valuePropName="checked"
          >
            <Switch checkedChildren="Ativo" unCheckedChildren="Inativo" />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button onClick={handleCancel}>
                Cancelar
              </Button>
              <Button type="primary" htmlType="submit">
                {editingEndpoint ? 'Atualizar' : 'Criar'}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Modal de Visualização de Endpoint */}
      <Modal
        title={`Detalhes do Endpoint: ${viewingEndpoint?.name}`}
        style={{ top: 0 }}
        open={viewModalVisible}
        onCancel={handleViewCancel}
        footer={[
          <Button key="close" onClick={handleViewCancel}>
            Fechar
          </Button>
        ]}
        width={700}
        maskClosable={false}
      >
        {viewingEndpoint && (
          <Descriptions bordered column={1}>
            <Descriptions.Item label="Nome">
              {viewingEndpoint.name}
            </Descriptions.Item>
            <Descriptions.Item label="Endpoint">
              <Text copyable={{ text: `${import.meta.env.VITE_API_URL}/v1/webhook/xml` }}>
                {import.meta.env.VITE_API_URL}/v1/webhook/xml
              </Text>
            </Descriptions.Item>
            <Descriptions.Item label="Token de Acesso">
              <Space>
                <Text code copyable={{ text: viewingEndpoint.token }}>
                  {viewingEndpoint.token}
                </Text>
                <Tooltip title="Use este token no header Authorization: Bearer [token]">
                  <Button type="link" icon={<SecurityScanOutlined />} size="small">
                    Info
                  </Button>
                </Tooltip>
              </Space>
            </Descriptions.Item>
            <Descriptions.Item label="Status">
              <Badge
                status={viewingEndpoint.isActive ? 'success' : 'default'}
                text={viewingEndpoint.isActive ? 'Ativo' : 'Inativo'}
              />
            </Descriptions.Item>
            <Descriptions.Item label="Origens Permitidas">
              {viewingEndpoint.allowedOrigins && viewingEndpoint.allowedOrigins.length > 0 ? (
                <Space wrap>
                  {viewingEndpoint.allowedOrigins.map((origin, index) => (
                    <Tag key={index}>{origin}</Tag>
                  ))}
                </Space>
              ) : (
                <Tag color="orange">Todas as origens</Tag>
              )}
            </Descriptions.Item>
            <Descriptions.Item label="Descrição">
              {viewingEndpoint.description || 'Sem Descrição'}
            </Descriptions.Item>
            <Descriptions.Item label="Último Uso">
              {viewingEndpoint.lastUsedAt
                ? new Date(viewingEndpoint.lastUsedAt).toLocaleString('pt-BR')
                : 'Nunca usado'
              }
            </Descriptions.Item>
            <Descriptions.Item label="Criado em">
              {new Date(viewingEndpoint.createdAt).toLocaleString('pt-BR')}
            </Descriptions.Item>
            <Descriptions.Item label="Atualizado em">
              {new Date(viewingEndpoint.updatedAt).toLocaleString('pt-BR')}
            </Descriptions.Item>
          </Descriptions>
        )}

        <Divider>Exemplo de Uso</Divider>
        <Alert
          message="Como usar este endpoint"
          description={
            <div>
              <p>Envie uma requisição POST para o endpoint com o token no header:</p>
              <pre style={{
                background: '#f5f5f5',
                padding: 12,
                borderRadius: 4,
                fontSize: '12px'
              }}>
                {`curl -X POST \\
  ${window.location.origin}/v1/webhook/xml \\
  -H "Authorization: Bearer ${viewingEndpoint?.token}" \\
  -H "Content-Type: application/json" \\
  -d '{\n    "chave": "35200114200166000146550010000000046623138",\n    "cdremetente": "123",\n    "cddestinatario": "456",\n    "peso": 10.5,\n    "m3": 0.5\n  }'`}
              </pre>
            </div>
          }
          type="info"
          showIcon
        />
      </Modal>
    </div>
  );
};

export default WebhooksManagement;